import PageNavigationFooter, {
  FooterLink,
  FooterLinkProps,
  NavigationDirection,
} from "@teamscheme/page-navigation-footer";
import { RelativeUrls, useRelativeUrls } from "./routing/ContractFormRoutes";
import { ContractPath } from "./routing/ContractPath";

export interface BaseProps
  extends Omit<FooterLinkProps, "linkTo" | "next" | "back" | "direction"> {
  direction: NavigationDirection;
}
export interface FromProps extends BaseProps {
  from: ContractPath;
}

export interface RelativeProps extends BaseProps {
  using: RelativeUrls;
}

export type RouterFooterLinkProps = FromProps | RelativeProps;

function FromLink({ from, direction, ...props }: FromProps) {
  const pages = useRelativeUrls(from);
  const nextUrl = pages.next;
  const prevUrl = pages.prev;
  const next = direction === "next";
  const back = direction === "back";
  const to = next ? nextUrl : back ? prevUrl : null;

  if (!to) throw new Error("Either 'next' or 'back' is required!");

  return (
    <FooterLink
      linkTo={to}
      direction={direction}
      next={next}
      back={back}
      {...props}
    />
  );
}

function RelativeLink({ using, direction, ...props }: RelativeProps) {
  const next = direction === "next";
  const back = direction === "back";
  const to = next ? using.next : back ? using.prev : null;

  if (!to) return null;

  return (
    <FooterLink
      linkTo={to}
      direction={direction}
      next={next}
      back={back}
      {...props}
    />
  );
}

export default function RouterFooterLink(props: RouterFooterLinkProps) {
  if ("using" in props) return <RelativeLink {...props} />;
  if ("from" in props) return <FromLink {...props} />;
  throw new Error("Unrecognized props");
}

export function RelativeNavigationFooter({ using }: { using: RelativeUrls }) {
  return (
    <PageNavigationFooter>
      <RouterFooterLink direction="back" using={using} />
      <RouterFooterLink direction="next" using={using} />
    </PageNavigationFooter>
  );
}
